/**
 * ListCard Component
 * Displays a shopping list with all its items
 */

import React, { useState, useMemo } from 'react';
import { Plus, ShoppingCart, Trash2, MoreVertical, Layers, ArrowUpDown } from 'lucide-react';
import { ItemRow } from './ItemRow';
import type { List, Item } from '../api/types';

interface ListCardProps {
  list: List;
  onToggleItem: (id: number) => void;
  onDeleteItem: (id: number) => void;
  onAddItem: (listId: number, name: string, category: string, quantity: number) => void;
  onDeleteList: (id: number) => void;
}

/**
 * Card component for displaying a shopping list with its items
 */
export function ListCard({ list, onToggleItem, onDeleteItem, onAddItem, onDeleteList }: ListCardProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [sortBy, setSortBy] = useState<'manual' | 'category'>('manual');
  const [newItemName, setNewItemName] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('Non classé');
  const [newItemQuantity, setNewItemQuantity] = useState(1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    onAddItem(list.id, newItemName.trim(), newItemCategory, newItemQuantity);
    setNewItemName('');
    setNewItemCategory('Non classé');
    setNewItemQuantity(1);
    setIsAdding(false);
  };

  const handleDeleteList = () => {
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer la liste "${list.name}" ?\n\nCette action est irréversible et supprimera tous les articles de cette liste.`
    );
    
    if (confirmDelete) {
      onDeleteList(list.id);
    }
    setShowMenu(false);
  };

  const activeItems = list.items?.filter(item => !item.checked) || [];
  const completedItems = list.items?.filter(item => item.checked) || [];

  // Group active items by category
  const groupedActiveItems = useMemo(() => {
    if (sortBy === 'manual') return null;
    
    const groups: Record<string, Item[]> = {};
    activeItems.forEach(item => {
      const cat = item.category || 'Non classé';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    
    return Object.entries(groups).sort((a, b) => {
      if (a[0] === 'Non classé') return 1;
      if (b[0] === 'Non classé') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [activeItems, sortBy]);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
      {/* List Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFD100] rounded-lg flex items-center justify-center">
              <ShoppingCart className="text-slate-900" size={20} />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">{list.name}</h3>
              <p className="text-gray-400 text-sm">
                {activeItems.length} article{activeItems.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Sort Toggle */}
            {activeItems.length > 1 && (
              <button
                onClick={() => setSortBy(prev => prev === 'manual' ? 'category' : 'manual')}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                aria-label={sortBy === 'manual' ? 'Trier par rayon' : 'Ordre d\'ajout'}
                title={sortBy === 'manual' ? 'Trier par rayon' : 'Ordre d\'ajout'}
              >
                {sortBy === 'manual' ? (
                  <Layers className="text-white" size={18} />
                ) : (
                  <ArrowUpDown className="text-white" size={18} />
                )}
              </button>
            )}
            
            <button
              onClick={() => setIsAdding(!isAdding)}
              className="w-10 h-10 bg-[#FFD100] hover:bg-[#ffe066] rounded-lg flex items-center justify-center transition-colors"
              aria-label="Ajouter un article"
            >
              <Plus className="text-slate-900" size={20} />
            </button>
            
            {/* Delete List Button */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Options de la liste"
              >
                <MoreVertical className="text-white" size={20} />
              </button>
              
              {showMenu && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowMenu(false)}
                  />
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-20">
                    <button
                      onClick={handleDeleteList}
                      className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                    >
                      <Trash2 size={16} />
                      <span className="font-semibold">Supprimer la liste</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Item Form */}
      {isAdding && (
        <form onSubmit={handleSubmit} className="p-4 bg-slate-50 border-b border-slate-200">
          <div className="space-y-3">
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Nom du produit"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              autoFocus
            />
            <div className="flex gap-3">
              <input
                type="text"
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                placeholder="Catégorie"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
              <input
                type="number"
                min="1"
                max="99"
                value={newItemQuantity}
                onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 bg-[#FFD100] hover:bg-[#ffe066] text-slate-900 font-bold py-2 px-4 rounded-lg transition-colors"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Active Items */}
      {activeItems.length > 0 && (
        <div className="p-4">
          {sortBy === 'manual' ? (
            // Manual order - flat list
            <div className="space-y-2">
              {activeItems.map(item => (
                <ItemRow
                  key={item.id}
                  item={item}
                  onToggle={onToggleItem}
                  onDelete={onDeleteItem}
                />
              ))}
            </div>
          ) : (
            // Grouped by category
            <div className="space-y-4">
              {groupedActiveItems?.map(([category, items]) => (
                <div key={category} className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
                  <div className="bg-[#FFD100] px-3 py-2 flex items-center justify-between">
                    <h4 className="text-slate-900 font-bold text-sm uppercase tracking-wider">
                      {category}
                    </h4>
                    <span className="bg-slate-900 text-[#FFD100] text-xs font-bold px-2 py-0.5 rounded-md">
                      {items.length}
                    </span>
                  </div>
                  <div className="p-2 space-y-2">
                    {items.map(item => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        onToggle={onToggleItem}
                        onDelete={onDeleteItem}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Completed Items */}
      {completedItems.length > 0 && (
        <div className="p-4 pt-0 space-y-2 opacity-60">
          <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 pt-4 border-t border-gray-100">
            Déjà au panier ({completedItems.length})
          </div>
          {completedItems.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              onToggle={onToggleItem}
              onDelete={onDeleteItem}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!activeItems.length && !completedItems.length && (
        <div className="p-8 text-center text-gray-400">
          <p className="text-sm">Aucun article dans cette liste</p>
          <button
            onClick={() => setIsAdding(true)}
            className="mt-3 text-[#FFD100] hover:text-[#ffe066] font-semibold text-sm"
          >
            Ajouter le premier article
          </button>
        </div>
      )}
    </div>
  );
}
